
'use client';

import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useState } from 'react';
import { motion } from 'framer-motion';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { WhiteLogo } from '../white-logo';
import { useToast } from '@/hooks/use-toast';
import { auth } from '@/lib/firebase';
import type { Locale } from '@/i18n-config';
import { cn } from '@/lib/utils';
import { Spinner } from '../ui/spinner';

export function ForgotPasswordForm({ lang, dict }: { lang: Locale; dict: any }) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const formSchema = z.object({
    email: z.string().email({ message: dict.emailInvalid }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    try {
      await sendPasswordResetEmail(auth, values.email);
      setEmailSent(true);
      toast({
        title: dict.resetEmailSentTitle,
        description: dict.resetEmailSentDescription,
      });
    } catch (error: any) {
      console.error('Password Reset Error:', error);
      let errorMessage = dict.resetEmailError;
       if (error.code === 'auth/user-not-found') {
        errorMessage = dict.resetEmailUserNotFound;
      }
      toast({
        variant: 'destructive',
        title: dict.resetEmailErrorTitle,
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="w-full max-w-md mx-auto bg-card backdrop-blur-sm border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
    >
      <div className="p-8">
        <div className="flex flex-col items-center justify-center mb-6">
          <WhiteLogo className="w-16 h-16 text-white" />
        </div>

        {emailSent ? (
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground">{dict.resetEmailSentTitle}</h1>
            <p className="text-muted-foreground mt-2">{dict.checkYourInbox}</p>
             <Button asChild className="mt-6 w-full">
              <Link href={`/${lang}/login`}>{dict.backToLogin}</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-foreground">{dict.forgotPasswordTitle}</h1>
              <p className="text-muted-foreground mt-2">{dict.forgotPasswordDescription}</p>
            </div>

            <div className="mt-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{dict.email}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="m@example.com"
                            {...field}
                            className="h-11"
                            disabled={isSubmitting}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <motion.div
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    className="pt-2"
                  >
                    <Button
                      type="submit"
                      className="w-full h-11 text-base font-semibold"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? <Spinner /> : dict.sendResetLinkButton}
                    </Button>
                  </motion.div>
                </form>
              </Form>
            </div>

            <div className="mt-8 text-center text-sm">
              <Link
                href={`/${lang}/login`}
                className={cn(
                  'font-semibold text-muted-foreground hover:text-foreground hover:underline',
                  isSubmitting && 'pointer-events-none opacity-50'
                )}
              >
                {dict.backToLogin}
              </Link>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
